import * as React from 'react'
import { ArrowDown, ArrowUp, CaseSensitive, ChevronsUpDown, Hash, List, Plus } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { Button } from '../../ui/Button'
import { ScrollArea } from '@/components/ui/ScrollArea'
import { DataTablePagination } from '../data-table/Pagination'
import type { TableState } from '../data-table/types'
import { SelectCell } from './SelectCell'
import type { GridColumn } from './types'

/* Grouping only — no rounding, no currency. A grid cell has to survive being
 * read back out as the same number it was given. */
const grouper = new Intl.NumberFormat('en-US', { maximumFractionDigits: 20 })

function groupNumber(text: string): string {
  if (text === '') return ''
  const value = Number(text)
  return Number.isFinite(value) ? grouper.format(value) : text
}

const TYPE_ICON = {
  read: Hash,
  text: CaseSensitive,
  number: Hash,
  select: List,
} as const

interface DataGridProps<T> {
  columns: readonly GridColumn<T>[]
  /** Exactly the rows to paint — already sorted and paginated. */
  rows: readonly T[]
  /** Row count before pagination, for the range label and pager. */
  total: number
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  rowKey: (row: T) => string
  /** Raw string from the cell; the caller decides how to coerce it. */
  onCellChange: (row: T, key: string, value: string) => void
  /**
   * Validation messages, keyed by row key then column key. A grid edits many
   * records at once, so an error has to point at the cell that caused it —
   * a banner saying "something is invalid" is unusable at 30 rows.
   */
  cellErrors?: Record<string, Record<string, string>>
  /** Trailing per-row control, typically a "…" menu — same slot, and the same
   *  shape, as <DataTable rowActions>. The page decides what is in it. */
  rowActions?: (row: T) => React.ReactNode
  onAddRow?: () => void
  addLabel?: string
  noun?: string
  /**
   * Paginate the rows. Config tables turn this off: a balance pass means
   * scanning the whole list, and paging hides half of it.
   */
  paginate?: boolean
  /** Height the body scrolls within once it outgrows it, in px. */
  maxHeight?: number
  className?: string
}

/**
 * Spreadsheet-style editable grid: every cell is an input, arrow keys and Enter
 * move between rows, and nothing is committed through a dialog.
 *
 * Distinct from <DataTable>, which is read-only with row actions. Both are
 * fully controlled — sorting and pagination happen outside.
 */
export function DataGrid<T>({
  columns,
  rows,
  total,
  state,
  onStateChange,
  rowKey,
  onCellChange,
  cellErrors,
  rowActions,
  onAddRow,
  addLabel = '+ New',
  noun = 'rows',
  paginate = true,
  maxHeight,
  className,
}: DataGridProps<T>) {
  const bodyRef = React.useRef<HTMLDivElement>(null)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  /* Adding a row is pointless if it lands out of sight. Watching the count
   * rather than taking a callback keeps the parent from having to tell us
   * when its state actually landed. */
  /* Which key is new, not which index is last. The rows handed in are sorted,
   * so a row added with a low sort value lands in the middle — the old
   * "focus the last index" guess then selected some unrelated card's cell and
   * left the real new row unfocused several rows up. */
  const seenKeys = React.useRef<Set<string> | null>(null)
  React.useEffect(() => {
    const keys = rows.map(rowKey)
    const previous = seenKeys.current
    seenKeys.current = new Set(keys)
    if (!previous) return

    const addedIndex = keys.findIndex((key) => !previous.has(key))
    if (addedIndex === -1) return

    const cell = bodyRef.current?.querySelector<HTMLInputElement>(
      `[data-cell="${addedIndex}:1"]`,
    )
    cell?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    cell?.focus()
    cell?.select()
  }, [rows, rowKey])

  const template = [...columns.map((column) => column.width), rowActions ? '56px' : '0px'].join(' ')

  /** Excel muscle memory: up/down walk the column, Enter steps down, Esc lets go. */
  /* A number column shows its thousands separators at rest and its raw value
   * the moment you focus it — grouping a string you are halfway through typing
   * fights the caret. maximumFractionDigits is wide open so a decimal survives
   * the round trip untouched. */
  const [editingCell, setEditingCell] = React.useState<string | null>(null)

  const focusAndSelect = (input: HTMLInputElement | null | undefined) => {
    if (!input) return
    input.focus()
    // After the commit that swaps the grouped value for the raw one, so the
    // selection covers what is actually there.
    requestAnimationFrame(() => input.select())
  }

  const handleKeyDown = (event: React.KeyboardEvent, rowIndex: number, columnIndex: number) => {
    const move = (delta: number) => {
      event.preventDefault()
      focusAndSelect(
        bodyRef.current?.querySelector<HTMLInputElement>(
          `[data-cell="${rowIndex + delta}:${columnIndex}"]`,
        ),
      )
    }

    if (event.key === 'ArrowDown' || event.key === 'Enter') move(1)
    else if (event.key === 'ArrowUp') move(-1)
    else if (event.key === 'Escape') (event.target as HTMLInputElement).blur()
  }

  const cellBase = 'flex min-w-0 items-center border-r border-border-subtle px-1 last:border-r-0'
  const inputBase =
    'w-full min-w-0 rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-base outline-none transition-colors duration-100 ease-out hover:bg-bg-hover focus:border-accent focus:bg-bg-elev'

  return (
    <div
      className={cn(
        'border-border bg-bg-elev overflow-hidden rounded-xl border shadow-2xs',
        className,
      )}
    >
      {/* <ScrollArea>, not native overflow: a native horizontal bar inside a
          tall card lands halfway down the page and behind whatever paints after
          it, which made it impossible to grab. Radix draws both bars as real
          elements, so they sit above the content and can be clicked. */}
      <ScrollArea maxHeight={maxHeight} viewportRef={scrollRef}>
        <div className="w-max min-w-full">
          {/* Header */}
          <div
            className="border-border bg-bg-subtle sticky top-0 z-10 grid h-9 border-b"
            style={{ gridTemplateColumns: template }}
          >
            {columns.map((column) => {
              const Icon = TYPE_ICON[column.type]
              const active = state.sortKey === column.key
              const Arrow = !active ? ChevronsUpDown : state.sortDir === 'asc' ? ArrowUp : ArrowDown

              return (
                <button
                  key={column.key}
                  type="button"
                  disabled={!column.sortable}
                  onClick={() =>
                    onStateChange({
                      sortKey: column.key,
                      sortDir: active && state.sortDir === 'asc' ? 'desc' : 'asc',
                      page: 1,
                    })
                  }
                  className={cn(
                    'group border-border-subtle flex w-full min-w-0 items-center gap-1.5 border-r px-2.5 text-sm font-semibold transition-colors duration-100 ease-out last:border-r-0',
                    column.align === 'end' && 'justify-end',
                    column.sortable ? 'hover:bg-bg-hover' : 'cursor-default',
                    active ? 'text-fg' : 'text-fg-muted',
                  )}
                >
                  {/* Right-aligned columns are numeric and narrow; the label
                      needs the room more than a glyph saying "number". */}
                  {column.align !== 'end' && <Icon className="text-fg-subtle size-3 shrink-0" />}
                  <span className="truncate">{column.header}</span>
                  {column.sortable && (
                    <Arrow
                      className={cn(
                        'size-3 shrink-0 transition-opacity duration-100 ease-out',
                        active ? 'opacity-100' : 'opacity-0 group-hover:opacity-60',
                      )}
                    />
                  )}
                </button>
              )
            })}
            <span />
          </div>

          {/* Rows */}
          <div>
            <div ref={bodyRef}>
              {rows.map((row, rowIndex) => (
                <div
                  key={rowKey(row)}
                  className="border-border-subtle hover:bg-bg-subtle grid h-(--height-row) border-b transition-colors duration-100 ease-out"
                  style={{ gridTemplateColumns: template }}
                >
                  {columns.map((column, columnIndex) => {
                    const raw = (row as Record<string, unknown>)[column.key]
                    const text = raw == null ? '' : String(raw)
                    const cellError = cellErrors?.[rowKey(row)]?.[column.key]
                    return (
                      <span
                        key={column.key}
                        className={cn(cellBase, column.align === 'end' && 'justify-end')}
                      >
                        {column.prefix?.(row)}

                        {column.type === 'read' && (
                          <span
                            className={cn(
                              'text-fg-muted truncate px-1.5',
                              column.align === 'end' && 'w-full text-right tabular-nums',
                            )}
                          >
                            {column.render ? column.render(row) : text}
                          </span>
                        )}

                        {column.type === 'select' && column.options && (
                          <SelectCell
                            invalid={!!cellError}
                            label={column.header}
                            value={text}
                            options={column.options}
                            onChange={(next) => onCellChange(row, column.key, next)}
                          />
                        )}

                        {(column.type === 'text' || column.type === 'number') && (
                          <input
                            data-cell={`${rowIndex}:${columnIndex}`}
                            value={
                              column.type === 'number' &&
                              editingCell !== `${rowIndex}:${columnIndex}`
                                ? groupNumber(text)
                                : text
                            }
                            inputMode={column.type === 'number' ? 'decimal' : undefined}
                            aria-label={`${column.header}, row ${rowIndex + 1}`}
                            onChange={(event) => onCellChange(row, column.key, event.target.value)}
                            onKeyDown={(event) => handleKeyDown(event, rowIndex, columnIndex)}
                            onFocus={(event) => {
                              setEditingCell(`${rowIndex}:${columnIndex}`)
                              focusAndSelect(event.currentTarget)
                            }}
                            onBlur={() => setEditingCell(null)}
                            aria-invalid={!!cellError}
                            title={cellError}
                            className={cn(
                              inputBase,
                              column.type === 'number' && 'text-right tabular-nums',
                              column.align === 'end' && 'text-right',
                              column.inputClassName,
                              cellError && 'border-err bg-err-surface',
                            )}
                          />
                        )}
                      </span>
                    )
                  })}

                  <span className="flex items-center justify-end pr-1.5">{rowActions?.(row)}</span>
                </div>
              ))}

              {rows.length === 0 && (
                <p className="text-fg-subtle m-0 px-4 py-10 text-center text-sm">
                  No {noun} match the current filters.
                </p>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>

      {/* One footer bar: the way to add a row sits with the count of what is
          already there, rather than as a phantom row the body has to scroll
          past. */}
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-t px-3 py-2.5">
        {onAddRow ? (
          <Button variant="outline" size="sm" onClick={onAddRow}>
            <Plus />
            {addLabel.replace(/^\+\s*/, '')}
          </Button>
        ) : (
          <span />
        )}

        {paginate ? (
          <DataTablePagination
            state={state}
            onStateChange={onStateChange}
            total={total}
            className="min-w-0 flex-1"
          />
        ) : (
          <span className="text-fg-muted text-sm tabular-nums">
            {total} {noun}
          </span>
        )}
      </div>
    </div>
  )
}

export type { DataGridProps }
